import React from 'react';
import '../../styles/InnovationChallenge.css';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import {
  Lightbulb, Users, Rocket, Award,
  CloudLightning, Landmark, HeartPulse, GraduationCap, TrendingUp,
  FileDown, ArrowRight, Mail, Calendar, CheckCircle2, Trophy,
} from 'lucide-react';
import data from '../../data/innovationChallenge';

// Maps the icon keys in the data file to components — keeps the data file
// free of JSX so it stays easy to edit.
const ABOUT_ICONS = { lightbulb: Lightbulb, users: Users, rocket: Rocket, award: Award };
const TRACK_ICONS = {
  climate: CloudLightning,
  government: Landmark,
  health: HeartPulse,
  education: GraduationCap,
  business: TrendingUp,
};

const STAGES = [
  { key: 'applications', label: 'Applications' },
  { key: 'top10', label: 'Top 10' },
  { key: 'top5', label: 'Top 5' },
  { key: 'winners', label: 'Winners' },
];

const PLACEMENT_LABEL = { 1: '1st', 2: '2nd', 3: '3rd' };

function scrollToCompetition() {
  const el = document.getElementById('ic-competition');
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// The hero call-to-action changes with the competition stage.
function HeroCta() {
  const { stage, applications } = data;

  if (stage === 'applications' && applications.open) {
    return (
      <a className='ic-cta' href={applications.applyUrl} target='_blank' rel='noopener noreferrer'>
        Apply Now <ArrowRight size={18} />
      </a>
    );
  }

  const label =
    stage === 'top10' ? 'View Top 10' :
    stage === 'top5' ? 'View Top 5' :
    stage === 'winners' ? 'Meet the Winners' :
    'See the Challenge';

  return (
    <button className='ic-cta' onClick={scrollToCompetition}>
      {label} <ArrowRight size={18} />
    </button>
  );
}

function CurrentStageIndex() {
  return STAGES.findIndex((s) => s.key === data.stage);
}

// Signature element: the lifecycle stepper.
function StageStepper() {
  const active = CurrentStageIndex();
  return (
    <div className='ic-stepper' role='list' aria-label='Competition progress'>
      {STAGES.map((s, i) => (
        <React.Fragment key={s.key}>
          <div
            role='listitem'
            className={`ic-step ${i < active ? 'is-done' : ''} ${i === active ? 'is-active' : ''}`}
            aria-current={i === active ? 'step' : undefined}
          >
            <span className='ic-step-dot' />
            <span className='ic-step-label'>{s.label}</span>
          </div>
          {i < STAGES.length - 1 && (
            <span className={`ic-step-line ${i < active ? 'is-done' : ''}`} aria-hidden='true' />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

// Clean information card used for both Top 10 and Top 5 (no photos by design).
function TeamCard({ t }) {
  return (
    <div className='ic-team-card'>
      <div className='ic-team-name'>{t.team}</div>
      <div className='ic-team-project'>{t.project}</div>
      <div className='ic-team-meta'>
        <span>{t.country}</span>
        <span className='ic-team-track'>{t.track}</span>
      </div>
    </div>
  );
}

// The section that changes shape as the competition advances.
function CompetitionSection() {
  const { stage, year, applications, teams, finalists } = data;

  if (stage === 'applications') {
    return (
      <section id='ic-competition' className='ic-section ic-comp'>
        <h2 className='ic-h2'>{year} Competition</h2>

        {applications.open ? (
          <a className='ic-cta ic-cta-lg' href={applications.applyUrl} target='_blank' rel='noopener noreferrer'>
            Apply Now <ArrowRight size={18} />
          </a>
        ) : (
          <div className='ic-notice'>Applications for {year} are closed — Top 10 announced soon.</div>
        )}

        <div className='ic-deadline'>
          <Calendar size={18} /> Application deadline: <strong>{applications.deadline}</strong>
        </div>

        <div className='ic-two-col'>
          <div className='ic-panel'>
            <h3 className='ic-h3'>Eligibility</h3>
            <ul className='ic-list'>
              {applications.eligibility.map((x, i) => (
                <li key={i}><CheckCircle2 size={16} /> {x}</li>
              ))}
            </ul>
          </div>
          <div className='ic-panel'>
            <h3 className='ic-h3'>Prize Highlights</h3>
            <ul className='ic-list'>
              {applications.prizes.map((x, i) => (
                <li key={i}><Trophy size={16} /> {x}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    );
  }

  if (stage === 'top10' || stage === 'top5') {
    const roster = stage === 'top5' ? teams.filter((t) => t.top5) : teams;
    const heading = stage === 'top5' ? `${year} Top 5` : `${year} Top 10`;
    return (
      <section id='ic-competition' className='ic-section ic-comp'>
        <h2 className='ic-h2'>{heading}</h2>
        {roster.length === 0 ? (
          <div className='ic-notice'>Teams announced soon.</div>
        ) : (
          <div className='ic-team-grid'>
            {roster.map((t, i) => <TeamCard key={i} t={t} />)}
          </div>
        )}
      </section>
    );
  }

  // stage === 'winners' → Top 3 finalists with photos + placement.
  return (
    <section id='ic-competition' className='ic-section ic-comp'>
      <h2 className='ic-h2'>{year} Winners</h2>
      <div className='ic-finalist-grid'>
        {[...finalists].sort((a, b) => a.placement - b.placement).map((f, i) => (
          <div key={i} className={`ic-finalist ic-place-${f.placement}`}>
            <div className='ic-place-badge'>{PLACEMENT_LABEL[f.placement]}</div>
            {f.photo && (
              <PhotoProvider>
                <PhotoView src={process.env.PUBLIC_URL + f.photo}>
                  <img className='ic-finalist-photo' src={process.env.PUBLIC_URL + f.photo} alt={`${f.team} team`} />
                </PhotoView>
              </PhotoProvider>
            )}
            <div className='ic-finalist-body'>
              <div className='ic-team-name'>{f.team}</div>
              <div className='ic-team-project'>{f.project}</div>
              <div className='ic-team-meta'>
                <span>{f.country}</span>
                <span className='ic-team-track'>{f.track}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function InnovationChallenge() {
  const { hero, about, tracks, hallOfWinners, gallery, sponsors, mentorship, contactEmail } = data;

  return (
    <div className='ic-page'>
      {/* 1. Hero */}
      <header className='ic-hero'>
        <div className='ic-hero-bg' aria-hidden='true'>
          <span className='ic-blob ic-blob-1' />
          <span className='ic-blob ic-blob-2' />
          <span className='ic-blob ic-blob-3' />
        </div>
        <div className='ic-hero-inner'>
          <div className='ic-eyebrow'>Caribbean Youth ICT Showcase</div>
          <img
            className='ic-hero-logo'
            src={process.env.PUBLIC_URL + '/assets/Innovation_Challenge_logo.png'}
            alt='CANTO Innovation Challenge'
          />
          <p className='ic-hero-intro'>{hero.intro}</p>
          <HeroCta />
        </div>
        <StageStepper />
      </header>

      {/* 2. About */}
      <section className='ic-section'>
        <h2 className='ic-h2'>About the Challenge</h2>
        <div className='ic-card-grid ic-about-grid'>
          {about.map((c, i) => {
            const Icon = ABOUT_ICONS[c.icon] || Lightbulb;
            return (
              <div className='ic-card' key={i}>
                <div className='ic-card-icon'><Icon size={26} /></div>
                <div className='ic-card-title'>{c.title}</div>
                <div className='ic-card-body'>{c.body}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 3. Innovation Tracks */}
      <section className='ic-section ic-tracks-section'>
        <h2 className='ic-h2'>Innovation Tracks</h2>
        <div className='ic-card-grid ic-tracks-grid'>
          {tracks.map((t, i) => {
            const Icon = TRACK_ICONS[t.icon] || Lightbulb;
            return (
              <div className='ic-card ic-track-card' key={i}>
                <div className='ic-card-icon'><Icon size={26} /></div>
                <div className='ic-card-title'>{t.title}</div>
                <div className='ic-card-body'>{t.body}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* 4. Dynamic Competition Section */}
      <CompetitionSection />

      {/* 5. Hall of Winners */}
      {hallOfWinners.length > 0 && (
        <section className='ic-section'>
          <h2 className='ic-h2'>Hall of Winners</h2>
          <div className='ic-hall-grid'>
            {hallOfWinners.map((w, i) => (
              <div className='ic-hall-card' key={i}>
                {w.photo && (
                  <PhotoProvider>
                    <PhotoView src={process.env.PUBLIC_URL + w.photo}>
                      <img className='ic-hall-photo' src={process.env.PUBLIC_URL + w.photo} alt={`${w.team}, ${w.year}`} />
                    </PhotoView>
                  </PhotoProvider>
                )}
                <div className='ic-hall-body'>
                  <div className='ic-hall-year'>{w.year}</div>
                  <div className='ic-team-name'>{w.team}</div>
                  <div className='ic-team-project'>{w.project} · {w.country}</div>
                  {w.summary && <p className='ic-hall-summary'>{w.summary}</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 6. Gallery */}
      {gallery.length > 0 && (
        <section className='ic-section'>
          <h2 className='ic-h2'>Gallery</h2>
          <PhotoProvider>
            <div className='ic-gallery'>
              {gallery.map((g, i) => (
                <PhotoView key={i} src={process.env.PUBLIC_URL + g.src}>
                  <figure className='ic-gallery-item'>
                    <img src={process.env.PUBLIC_URL + g.src} alt={g.caption || ''} />
                    {g.caption && <figcaption>{g.caption}</figcaption>}
                  </figure>
                </PhotoView>
              ))}
            </div>
          </PhotoProvider>
        </section>
      )}

      {/* 7. Sponsors & Partners */}
      {sponsors.length > 0 && (
        <section className='ic-section'>
          <h2 className='ic-h2'>Sponsors & Partners</h2>
          <div className='ic-sponsors'>
            {sponsors.map((s, i) => {
              const logo = <img src={process.env.PUBLIC_URL + s.logo} alt={s.name} />;
              return s.url ? (
                <a key={i} className='ic-sponsor' href={s.url} target='_blank' rel='noopener noreferrer'>{logo}</a>
              ) : (
                <div key={i} className='ic-sponsor'>{logo}</div>
              );
            })}
          </div>
        </section>
      )}

      {/* Mentorship callout */}
      {mentorship && (
        <section className='ic-section ic-mentor'>
          <h2 className='ic-h2'>{mentorship.title}</h2>
          <p className='ic-mentor-lead'>{mentorship.lead}</p>
          <p className='ic-mentor-body'>{mentorship.body}</p>
          {mentorship.guideUrl && (
            <a
              className='ic-cta'
              href={process.env.PUBLIC_URL + mentorship.guideUrl}
              target='_blank'
              rel='noopener noreferrer'
            >
              <FileDown size={18} /> Mentorship Programme
            </a>
          )}
        </section>
      )}

      {/* 8. Contact */}
      <section className='ic-contact'>
        <h2 className='ic-contact-title'>Questions about the Innovation Challenge?</h2>
        <a className='ic-contact-mail' href={`mailto:${contactEmail}`}>
          <Mail size={18} /> {contactEmail}
        </a>
      </section>
    </div>
  );
}
